import { useCallback, useState } from 'react';
import { Box, Tabs, tabsClasses, useMediaQuery } from '@mui/material';
import { sub } from 'date-fns';
import { makeStyles } from 'tss-react/mui';

import { TabPanel, ZERO } from 'modules/common';
import { useTranslation } from 'modules/i18n';

import { useFormValues } from '../../hooks/useFormValues';
import { TaskTab, useTaskTabs } from '../../hooks/useTaskTabs';
import { ConfirmForm } from '../ConfirmForm';
import { ExecuteForm, ExecuteFormSubmitParams } from '../ExecuteForm';
import { PaymentForm } from '../PaymentForm';
import { Tab } from '../Tab';
import { WhenForm, WhenFormSubmitParams } from '../WhenForm';
import translation from './taskForms.translation';

export type SubmitState = ExecuteFormSubmitParams & WhenFormSubmitParams;

interface Props {
  onSubmit: (state: SubmitState) => Promise<void>;
  isLoading?: boolean;
}

export function TaskForms({ onSubmit, isLoading }: Props): JSX.Element {
  const { t, keys } = useTranslation(translation);
  const { classes, cx, theme } = useStyles();
  const { formValues, updateFormValues } = useFormValues();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [submitStateValues, setSubmitState] = useState<SubmitState>(() => ({
    taskName: '',
    taskContractAddress: '',
    gasLimit: ZERO,
    abi: [],
    abiItem: null,
    params: [],
    cronExpression: '',
    startDate: new Date(),
    startImmediately: true,
    isCompatible: false,
    isCustomAbi: false,
    customData: null,
  }));
  const {
    isCompatible,
    taskName,
    taskContractAddress,
    abiItem,
    params,
    startDate,
    startImmediately,
    cronExpression,
  } = submitStateValues;
  const { currentTab, changeTab, passedTabs } = useTaskTabs();

  const isDisabledTab = (tab: TaskTab): boolean =>
    !passedTabs.has(tab) && tab !== currentTab;

  const handleUpdateSubmitState = useCallback(
    (nextTab: TaskTab) =>
      (params: Partial<SubmitState> = {}): void => {
        setSubmitState(state => ({
          ...state,
          ...params,
        }));
        changeTab(nextTab);
      },
    [changeTab],
  );

  const handleSubmit = async (): Promise<void> => {
    const { startDate } = submitStateValues;
    await onSubmit({
      ...submitStateValues,
      // needs for correct cron interval start
      startDate: startDate ? sub(startDate, { seconds: 1 }) : startDate,
    });
  };

  return (
    <div data-tid="create-task">
      <Box mt={8} mb={8}>
        <Tabs
          data-tid="create-task-tabs"
          className={classes.tabs}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          value={currentTab}
        >
          <Tab
            data-tid={`create-task-tab-${TaskTab.Execute}`}
            className={cx(
              classes.tab,
              passedTabs.has(TaskTab.Execute) && classes.passedTab,
              TaskTab.Execute === currentTab && classes.borderedTab,
            )}
            label={t(keys.executeLabel)}
            value={TaskTab.Execute}
            disabled={isDisabledTab(TaskTab.Execute)}
            onClick={() => changeTab(TaskTab.Execute)}
            passed={passedTabs.has(TaskTab.Execute)}
          />

          <Tab
            data-tid={`create-task-tab-${TaskTab.When}`}
            className={cx(
              classes.tab,
              passedTabs.has(TaskTab.When) && classes.passedTab,
              TaskTab.When === currentTab && classes.borderedTab,
            )}
            label={t(keys.whenLabel)}
            value={TaskTab.When}
            disabled={isDisabledTab(TaskTab.When)}
            onClick={() => changeTab(TaskTab.When)}
            passed={passedTabs.has(TaskTab.When)}
          />

          <Tab
            data-tid={`create-task-tab-${TaskTab.Payment}`}
            className={cx(
              classes.tab,
              passedTabs.has(TaskTab.Payment) && classes.passedTab,
              TaskTab.Payment === currentTab && classes.borderedTab,
            )}
            label={t(keys.paymentLabel)}
            value={TaskTab.Payment}
            disabled={isDisabledTab(TaskTab.Payment)}
            onClick={() => changeTab(TaskTab.Payment)}
            passed={passedTabs.has(TaskTab.Payment)}
          />

          <Tab
            data-tid={`create-task-tab-${TaskTab.Confirm}`}
            className={cx(
              classes.tab,
              passedTabs.has(TaskTab.Confirm) && classes.passedTab,
              TaskTab.Confirm === currentTab && classes.borderedTab,
            )}
            label={t(keys.confirmLabel)}
            value={TaskTab.Confirm}
            disabled={isDisabledTab(TaskTab.Confirm)}
            onClick={() => changeTab(TaskTab.Confirm)}
            passed={passedTabs.has(TaskTab.Confirm)}
          />
        </Tabs>
      </Box>

      <TabPanel value={TaskTab.Execute} index={currentTab}>
        <ExecuteForm
          onSubmit={handleUpdateSubmitState(TaskTab.When)}
          initialFormValues={formValues.executeForm}
          onSaveFormValues={values => updateFormValues('executeForm', values)}
        />
      </TabPanel>

      <TabPanel value={TaskTab.When} index={currentTab}>
        <WhenForm
          isCompatible={isCompatible}
          onSubmit={handleUpdateSubmitState(TaskTab.Payment)}
          initialFormValues={formValues.whenForm}
          onSaveFormValues={values => updateFormValues('whenForm', values)}
        />
      </TabPanel>

      <TabPanel value={TaskTab.Payment} index={currentTab}>
        <PaymentForm onSubmit={handleUpdateSubmitState(TaskTab.Confirm)} />
      </TabPanel>

      <TabPanel value={TaskTab.Confirm} index={currentTab}>
        <ConfirmForm
          taskName={taskName}
          taskContractAddress={taskContractAddress}
          taskAbi={abiItem}
          params={params}
          startDate={startDate}
          startImmediately={startImmediately}
          cronDate={cronExpression}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </TabPanel>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  tabs: {
    '&&': {
      [`& .${tabsClasses.flexContainer}`]: {
        gap: theme.spacing(2),
      },

      [theme.breakpoints.down('sm')]: {
        width: `calc(100% + ${theme.spacing(8)})`,
        marginLeft: theme.spacing(-4),

        [`& .${tabsClasses.scroller}`]: {
          display: 'flex',
          justifyContent: 'center',
        },

        [`& .${tabsClasses.flexContainer}`]: {
          gap: theme.spacing(1),

          '&:before': {
            content: '""',
            paddingLeft: theme.spacing(4),
          },

          '&:after': {
            content: '""',
            paddingRight: theme.spacing(4),
          },
        },
      },
    },
  },
  tab: {
    '&&': {
      position: 'relative',
      padding: theme.spacing(2.5, 3),
      whiteSpace: 'nowrap',
    },
  },
  passedTab: {
    '&&': {
      color: theme.palette.grey[900],
    },
  },
  borderedTab: {
    '&:after': {
      position: 'absolute',
      bottom: 0,
      left: 0,
      content: '""',
      height: 2,
      width: '100%',
      backgroundColor: theme.palette.primary.main,
      borderRadius: theme.spacing(0.5),
    },
  },
}));
